import styled from 'styled-components';
import type { LanguageValue } from '@elfsight/configurator-contracts';
import { Show } from '@elfsight/lib-utils';
import { MediaImage } from '@elfsight/widget-media';
import type { PriceProps } from '@elfsight/widget-ui';
import { getTokenFontStyle, Typography } from '@elfsight/widget-ui';
import { createReadableClass } from '@elfsight/widget-utils';
import { useSettings } from '@wu/widget-core';
import {
  useCheckoutColors,
  useCheckoutI18n,
  useCheckoutItem
} from '../providers';
import { DividedItem } from './divided-item';
import { InvalidPrice } from './invalid-price';
import { OrderSummaryButton } from './text-button-variants';

const COMPONENT_NAME = 'checkout-item';

export interface CheckoutItemProps {
  label?: string;
  price?: PriceProps;
  imageSrc?: string;
}

export function CheckoutItem() {
  const { language } = useSettings<{ language: LanguageValue }>();
  const { textColor, accentColor } = useCheckoutColors();
  const { PriceComponent } = useCheckoutI18n();
  const { label, price, imageSrc } = useCheckoutItem();

  const withImage = !!imageSrc;
  const withLabel = !!label;
  const withPrice = !!price;

  return (
    <DividedItem>
      <Component>
        <Content>
          <Show when={withImage}>
            <ItemImage
              src={imageSrc}
              alt={label}
              mode="cover"
              containerWidth="40px"
              containerHeight="40px"
              withSkeleton
            />
          </Show>

          <Info>
            <Show when={withLabel}>
              <Typography
                textToken="text16Bold"
                color={textColor}
                className={createReadableClass({
                  component: COMPONENT_NAME,
                  element: 'label'
                })}
              >
                {label}
              </Typography>
            </Show>

            <Show when={withPrice}>
              <PriceComponent
                {...price!}
                language={language}
                valueColor={textColor}
                affixColor={textColor}
                valueFont={getTokenFontStyle('text16')}
                className={createReadableClass({
                  component: COMPONENT_NAME,
                  element: 'price'
                })}
              />
            </Show>

            <Show when={!withPrice}>
              <InvalidPrice componentName={COMPONENT_NAME} />
            </Show>
          </Info>
        </Content>

        <OrderSummaryButton color={accentColor} />
      </Component>
    </DividedItem>
  );
}

const Component = styled.div.attrs({
  className: createReadableClass({
    component: COMPONENT_NAME
  })
})`
  display: flex;
  flex-direction: row;
  gap: 10px;

  width: 100%;
  max-width: 100%;
`;

const Content = styled.div.attrs({
  className: createReadableClass({
    component: COMPONENT_NAME,
    element: 'content'
  })
})`
  display: flex;
  align-items: center;
  gap: 12px;

  width: 100%;
`;

const ItemImage = styled(MediaImage).attrs({
  className: createReadableClass({
    component: COMPONENT_NAME,
    element: 'image'
  })
})`
  flex-shrink: 0;
  border-radius: 6px;
  overflow: hidden;
`;

const Info = styled.div.attrs({
  className: createReadableClass({
    component: COMPONENT_NAME,
    element: 'info'
  })
})`
  display: flex;
  flex-direction: column;
  flex: 1;

  min-width: 0;
`;
